/**
 * Mijoz kabineti API.
 *
 * Kabinetning o'z endpointlari `/client/...` da (jamlanma, xizmatlar katalogi,
 * murojaatlar). Shartnoma, invoys, uchastka va buyurtma ro'yxatlari esa
 * odatdagi domen endpointlaridan o'qiladi — backend ularni foydalanuvchi
 * bo'yicha (`billing.view_own`, `lessee`, `buyer`) allaqachon filtrlaydi,
 * shuning uchun mijoz begona yozuvni ko'ra olmaydi.
 */
import { api } from "./client";
import { qs } from "./endpoints";
import type { Query } from "./endpoints";
import type { LeasePassport, Paginated } from "./types";

/* ------------------------------------------------------------------ turlar */

export interface ClientCapabilities {
  billing: boolean;
  lease: boolean;
  market: boolean;
  buy: boolean;
  appeals: boolean;
}

export interface ClientOverview {
  profile: {
    full_name: string;
    short_name: string;
    email: string;
    phone: string;
    tin: string;
    subject_type: string;
    subject_type_label: string;
    organization: string;
    roles: string[];
  };
  capabilities: ClientCapabilities;
  appeals: { total: number; open: number; closed: number } | null;
  billing: {
    contracts: number;
    active_contracts: number;
    invoices: number;
    unpaid_invoices: number;
    overdue_invoices: number;
    debt: number;
    paid_total: number;
    next_due_date: string | null;
    next_due_amount: number;
    next_invoice_number: string;
  } | null;
  lease: {
    plots: number;
    area_ha: number;
    expiring_soon: number;
    expired: number;
    open_violations: number;
  } | null;
  market: { orders: number; active_orders: number; cart_items: number } | null;
  generated_at: string;
}

/* --------------------------------------------------------- ijara turlari */

/** `GET /client/lease/plots/` dagi bitta satr. */
export interface ClientLeasePlot {
  id: string;
  plot_id: string;
  status: string;
  status_label: string;
  registry_status: string;
  registry_status_label: string;
  registry_color: string;
  lease_type_label: string;
  area_ha: number;
  cadastre_number: string;
  compartment_label: string;
  region: string;
  forestry: string;
  purpose: string;
  contract_number: string;
  contract_start: string | null;
  contract_end: string | null;
  contract_amount: number;
  days_to_expiry: number | null;
  financial_status: string;
  monitoring_status: string;
  monitoring_status_label: string;
  next_monitoring_date: string | null;
  open_violations: number;
}

export interface ClientLeaseSummary {
  plots: number;
  area_ha: number;
  active: number;
  expiring_soon: number;
  expired: number;
  debt: number;
  open_violations: number;
  next_monitoring_date: string | null;
}

export interface ClientLeaseNotice {
  id: number;
  plot_id: string;
  reason: string;
  reason_label: string;
  stage: number;
  subject: string;
  body: string;
  sent_at: string;
}

export interface ClientLeaseViolation {
  id: string;
  plot: string;
  plot_id: string;
  act_number: string;
  act_date: string | null;
  kind_label: string;
  severity: string;
  status: string;
  status_label: string;
  description: string;
  fix_deadline: string | null;
  is_overdue: boolean;
}

export interface ClientService {
  code: string;
  title: string;
  description: string;
  icon: string;
  kind: "internal" | "external";
  action: string;
  route?: string;
  url?: string;
}

export interface ClientAppeal {
  id: string;
  appeal_id: string;
  type: string;
  type_code: string;
  status: string;
  status_label: string;
  risk_level: string;
  created_at: string;
  address_text: string;
  region: string;
  district: string;
  deadline_at: string | null;
  result_tag: string;
  description?: string;
  result_note?: string;
  closed_at?: string | null;
  coordinates?: [number, number] | null;
  history?: Array<{ from: string; to: string; reason: string; created_at: string }>;
}

export interface AppealTypeOption {
  id: number;
  code: string;
  name: string;
  color: string;
}

export interface CatalogItem {
  id: string;
  code: string;
  name: string;
  description: string;
  category: string;
  product_type: string;
  price: string;
  unit: string;
  available: string;
  min_order: string;
  seller: string;
  seller_region: string;
  /** O'qiladigan nomlar (ochiq katalog beradi); kodlar yuqorida. */
  product_type_name?: string;
  category_name?: string;
  seller_name?: string;
  region_name?: string;
  images?: string[];
  specifications?: Record<string, unknown>;
  certificates?: string[];
  compatibility?: string;
}

export interface CartItem {
  id: number;
  product: string;
  product_code: string;
  product_name: string;
  quantity: string;
  unit_price: string;
  subtotal: string;
}

export interface Cart {
  id: string;
  items: CartItem[];
  total: string;
}

/* --------------------------------------------------------------------- API */

export const clientApi = {
  overview: () => api.get<ClientOverview>("/client/overview/"),

  services: () =>
    api.get<{ internal: ClientService[]; external: ClientService[] }>("/client/services/"),

  appeals: (params: Query = {}) =>
    api.get<{ count: number; results: ClientAppeal[] }>(`/client/appeals/${qs(params)}`),

  appeal: (appealId: string) => api.get<ClientAppeal>(`/client/appeals/${appealId}/`),

  appealTypes: () => api.get<AppealTypeOption[]>("/client/appeals/types/"),

  createAppeal: (payload: {
    appeal_type: number;
    description: string;
    lat: number;
    lon: number;
    address_text?: string;
    risk_level?: string;
  }) => api.post<ClientAppeal>("/client/appeals/", payload),

  /* --- ijara uchastkalari (TZ 2-3, 11-15-bo'limlar) --- */

  /** Uchastkalar ro'yxati va portfel yakunlari. */
  leasePlots: (params: Query = {}) =>
    api.get<{ count: number; results: ClientLeasePlot[]; summary: ClientLeaseSummary }>(
      `/client/lease/plots/${qs(params)}`,
    ),

  /** Uchastkaning elektron pasporti — mijoz ko'rinishida. */
  leasePassport: (plotId: string) =>
    api.get<LeasePassport>(`/client/lease/plots/${plotId}/`),

  /** Ogohlantirish, eslatma va ochiq qoidabuzarliklar. */
  leaseNotices: () =>
    api.get<{ notices: ClientLeaseNotice[]; violations: ClientLeaseViolation[] }>(
      "/client/lease/notices/",
    ),
};

/** «Yashil olam» — katalog, savat va buyurtma. */
export interface PublicService {
  id: number;
  code: string;
  name: string;
  description: string;
  category: string;
  category_name: string;
  unit: string;
  unit_name: string;
  price: number;
  currency: string;
}

export const shopApi = {
  publicServices: (lang?: string) =>
    api.get<{ count: number; results: PublicService[] }>(`/market/public-services/${qs({ lang })}`),

  catalog: (params: Query = {}) =>
    api.get<{ count: number; results: CatalogItem[] }>(`/market/catalog/${qs(params)}`),

  cart: () => api.get<Cart>("/market/cart/"),

  addToCart: (product: string, quantity: number) =>
    api.post<Cart>("/market/cart/items/", { product, quantity }),

  removeFromCart: (itemId: number) => api.del<Cart>(`/market/cart/items/${itemId}/`),

  clearCart: () => api.post<{ detail: string }>("/market/cart/clear/"),

  createOrder: (payload: {
    items: Array<{ product_id: string; quantity: number }>;
    delivery_address?: string;
  }) => api.post<Record<string, unknown>>("/market/orders/", payload),

  orders: (params: Query = {}) =>
    api.get<Paginated<Record<string, unknown>>>(`/market/orders/${qs(params)}`),

  /** Buyurtma invoysini onlayn to'lash: to'lov sahifasi havolasi qaytadi. */
  payInvoice: (invoiceId: string) =>
    api.post<{ payment_url: string; external_id: string }>(
      `/billing/invoices/${invoiceId}/pay-online/`,
      { payment_system: "payme" },
    ),
  confirmReceipt: (orderId: string) =>
    api.post<Record<string, unknown>>(`/market/orders/${orderId}/confirm-receipt/`),
  reportIssue: (orderId: string, payload: { kind: string; description: string }) =>
    api.post<Record<string, unknown>>(`/market/orders/${orderId}/issue/`, payload),
  sandboxConfirm: (tx: string) =>
    api.post<{ detail: string }>("/billing/sandbox-confirm/", { tx }),
};
